import {
    Container,
    Sprite,
    NineSlicePlane,
    filters
} from 'pixi.js';
import { UIBoxBG } from './UIBoxBG';
import { Focusable, fonts, StyledBitmapText } from '../../ui';
import { LoadContext, globals, Disposable } from '../../utils';
import { GlowFilter } from '@pixi/filter-glow';

// width for focus frame
const FRAME_WIDTH = 6;

const DESATURATE = new filters.ColorMatrixFilter();
// desired color: 6766cc
DESATURATE.matrix[0] = 0; // Red, multiplicative (zero out for full additive replacement)
DESATURATE.matrix[4] = 0x67 / 255; // Red, additive (our desired color)
DESATURATE.matrix[6] = 0; // Green, multiplicative (zero out for full additive replacement)
DESATURATE.matrix[9] = 0x66 / 255; // Green, additive (our desired color)
DESATURATE.matrix[12] = 0; // Blue, multiplicative (zero out for full additive replacement)
DESATURATE.matrix[14] = 0xcc / 255; // Blue, additive (our desired color)

const GLOW = new GlowFilter({
    distance: 3,
    color: 0xffffff,
    outerStrength: 10,
    quality: 0.2 // important to have quality greater than 0.1 (the default) to avoid artifacts
});

const GLOW_LOW = new GlowFilter({
    distance: 3,
    color: 0xffffff,
    outerStrength: 10,
    quality: 0.1 // use lower, default quality on weaker devices
});

export class PartsFound extends Container implements Focusable
{
    private bg: UIBoxBG;
    private label: StyledBitmapText;
    private partNames: string[];
    private parts: Sprite[];
    private found: boolean[];
    private focusBox: NineSlicePlane;
    private perfListener: Disposable;

    public static preload()
    {
        const loader = LoadContext.current;
        loader.add('SharedUI', './assets/shared/SharedUI{size}.json');
        loader.add('GameAtlas', './assets/game/GameAtlas{size}.json');

        UIBoxBG.preload();
    }

    constructor(names: string[], atlas:string)
    {
        super();

        const loader = LoadContext.current;

        this.bg = new UIBoxBG(528, 110);
        this.addChild(this.bg);

        this.label = new StyledBitmapText('Parts to Find: 3', fonts.GameLabel);
        // expected max width: 207
        this.label.anchor.y = 0.6;
        this.label.position.set(33, 55);
        this.addChild(this.label);

        this.partNames = names;
        this.parts = [];
        this.found = [];
        for (let i = 0; i < names.length; ++i)
        {
            const part = new Sprite(loader.getTexture(names[i], atlas));
            part.anchor.set(0.5);
            // spacing is built around a 632x548 expected size (original distractor art)
            part.scale.set(Math.min(58 / part.width, 50 / part.height));
            part.position.set(316 + 70 * i, 55);
            this.addChild(part);
            this.parts.push(part);

            if (globals.IS_CANVAS)
            {
                part.tint = 0x777777;
            }
            else
            {
                part.filters = [DESATURATE];
            }

            this.found.push(false);
        }

        this.focusBox = new NineSlicePlane(loader.getSpritesheet('SharedUI').textures['highlight_frame'], FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH);
        const {width, height} = this.bg;
        // make box just a tad bigger than whatever the size is
        this.focusBox.width = width + 2;
        this.focusBox.height = height + 2;
        this.focusBox.position.set(-1, -1);
        this.addChild(this.focusBox);
        this.focusBox.visible = false;

        this.perfListener = Disposable.fromPropertySubscription(globals.LOW_PERFORMANCE, (val) => {
            for (let i = 0; i < this.found.length; ++i)
            {
                if (this.found[i])
                {
                    this.parts[i].filters = [val ? GLOW_LOW : GLOW];
                }
            }
        });

        this.interactive = true;
    }

    public get focus() { return this.focusBox.visible; }
    public set focus(focused) {
        this.focusBox.visible = focused;
        if (focused)
        {
            this.emit('pointerover');
        }
        else
        {
            this.emit('pointerout');
            this.emit('unfocused');
        }
    }

    public get focusVisible() { return this.focusBox.visible; }
    public set focusVisible(visible) {
        this.focusBox.visible = visible;
    }

    public activate()
    {
        // no op here
    }

    public findPart(name:string)
    {
        const index = this.partNames.indexOf(name);
        // remove desaturate filter from that specific part
        if (index > -1)
        {
            if (globals.IS_CANVAS)
            {
                this.parts[index].tint = 0xffffff;
            }
            else
            {
                this.parts[index].filters = [globals.LOW_PERFORMANCE.value ? GLOW_LOW : GLOW];
            }
            this.found[index] = true;
        }
        this.label.text = `Parts to Find: ${3 - this.count}`;
    }

    public get count()
    {
        let count = 0;
        for (let i = 0; i < this.parts.length; ++i)
        {
            if (this.found[i])
            {
                ++count;
            }
        }
        return count;
    }

    public destroy()
    {
        this.perfListener.dispose();
        super.destroy({children: true});
    }
}